// features/projects/templates/continuum/finding-next-moves.js
// ═══════════════════════════════════════════════════════════
// FINDING NEXT MOVES — converts each Continuum finding into a
// ranked list of "what to do next" actions.
//
// The Findings tab already renders rich data per finding (plain-
// English finding · ecological prescription · human translation
// · gap analysis). What was missing: the *actionable* layer.
// Given this finding's current state, what's the highest-leverage
// thing the researcher should do?
//
// This module is pure rule-based logic — reads existing comp + dyn
// data, generates moves, ranks them. No API calls, no Claude API
// costs. Wires into the expanded ScorecardRow in findings.js.
//
// Some actions are AVAILABLE today; others are COMING in later
// sessions (Phase A in-app, parallel hypothesis registration,
// forward-prediction logger, brief generator). Marked accordingly
// so the user sees what's wired vs scheduled.
//
// Per docs/SIMULATOR_FOR_REGULAR_PEOPLE.md Pillar 2 — "What you
// should do next" is part of the accessibility translation layer.
//
// Phase 1 Session 1 of the Continuum-actionability work
// (per the 5-extension plan after the regular-people docs).
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { C, FONT as F } from './tokens';

// ─── Color cues per priority ──────────────────────────────────
// findings.js uses Continuum cool palette (C). This panel matches.
const PRIORITY_COLOR = {
  high:   C.amber || C.gold,   // important next move
  medium: C.gold,                // worthwhile but not blocking
  low:    C.dim,                 // nice-to-have
};

const STATUS_COLOR = {
  available: C.sage,    // wired and clickable
  coming:    C.dim,     // scheduled for a later session
};

// ─── Rules engine ─────────────────────────────────────────────
// Each rule examines the comparison + dynamic state and returns a
// next-move object if applicable. Rules run independently and
// produce a deduplicated, ranked list.

export function generateNextMoves({ comp, dyn, hasParallelHypothesis = false }) {
  const moves = [];
  if (!comp || !dyn) return moves;

  const verdict = (comp.verdict || '').toLowerCase();
  const phaseAStatus = comp.phase_a_status || null;
  const phaseARan = !!comp.phase_a_run_at;
  const confidence = (comp.confidence || '').toLowerCase();
  const gaps = dyn.gaps || {};
  const criticalGaps = Object.entries(gaps)
    .filter(([, sev]) => sev === 'critical')
    .map(([k]) => k);

  // ── Rule 1: Verdict reached, but no Phase A red-team yet ─────
  // Highest-leverage move when a finding has a verdict but hasn't
  // been stress-tested. The whole point of Phase A is to surface
  // hidden weaknesses BEFORE a finding is treated as concrete.
  // Wired as AVAILABLE in Session 2 — calls /api/continuum/phase-a/run.
  //
  // Suppress when phase_a_run_at is set but status is null — the
  // run already happened and module output was stored, but the
  // aggregator couldn't pin a verdict. Don't burn another $0.20 on
  // re-run; surface the ambiguous-result panel instead (below).
  if (verdict && verdict !== 'pending' && verdict !== 'error' &&
      verdict !== 'insufficient_data' && !phaseAStatus && !phaseARan) {
    moves.push({
      id: 'run-phase-a',
      priority: 'high',
      label: 'Run Phase A red-team on this finding',
      why: 'Counter-Narrative · Outside View · Root Cause Engine — surface hidden weaknesses before treating this as concrete.',
      duration: '~30 sec · ~$0.20 in API',
      status: 'available',
    });
  }

  // ── Rule 2: Critical gap is blocking the finding ─────────────
  // If the gap analysis shows any "critical" severity, that's a
  // blocker. Without resolving it, downstream actions don't help.
  if (criticalGaps.length > 0) {
    moves.push({
      id: 'resolve-critical-gap',
      priority: 'high',
      label: `Address the critical ${criticalGaps.length === 1 ? 'gap' : 'gaps'} blocking this finding`,
      why: `Critical: ${criticalGaps.join(', ')}. Findings traceable through unresolved critical gaps cannot graduate confidence.`,
      duration: 'varies · 1-3 sessions of research work',
      status: 'available',
    });
  }

  // ── Rule 3: Has a human translation but no paired hypothesis ─
  // The Continuum's whole premise is the eco↔human parallel.
  // When `human_translation` exists but no parallel hypothesis is
  // registered, the cross-system payoff is sitting unrealized.
  // Wired as AVAILABLE in Session 5 — opens the inline registration form.
  if (dyn.human_translation && !hasParallelHypothesis) {
    moves.push({
      id: 'register-parallel-hypothesis',
      priority: 'medium',
      label: 'Register the human-system parallel hypothesis',
      why: 'Convert the human translation into a paired hypothesis testable against human-system data. Activates the cross-domain analog.',
      duration: '~3 min',
      status: 'available',
    });
  }

  // ── Rule 4: PARALLEL or NEAR-PARALLEL + Phase A passed → log
  // forward prediction. Findings that survived stress-test should
  // generate testable forward-claims to enter the calibration
  // registry. NEAR-PARALLEL counts. SURVIVED-RESPECIFIED also counts
  // — the respec'd claim is still testable (often sharper than the
  // original because Phase A forced it to be more falsifiable).
  // Wired as AVAILABLE in Session 3 — opens the inline logger form.
  if ((verdict === 'parallel' || verdict === 'near-parallel') &&
      (phaseAStatus === 'survived' || phaseAStatus === 'survived-respecified')) {
    moves.push({
      id: 'log-forward-prediction',
      priority: 'medium',
      label: 'Log a forward prediction from this finding',
      why: 'Convert the finding into a testable claim with a resolution date. Builds a track record over time.',
      duration: '~2 min',
      status: 'available',
    });
  }

  // ── Rule 5: High-confidence + ecological prescription → brief ─
  // Findings that are both confident and have an actionable
  // prescription are ready to package for stakeholder audiences.
  // PARALLEL or NEAR-PARALLEL both count.
  if ((confidence === 'high' || confidence === 'med-high') &&
      (verdict === 'parallel' || verdict === 'near-parallel') &&
      dyn.ecological_prescription) {
    moves.push({
      id: 'generate-donor-brief',
      priority: 'low',
      label: 'Generate a donor brief from this finding',
      why: 'Funder-ready 1-page format · evidence summary · cost+impact framing · ready to share.',
      duration: '~10 min',
      status: 'available',
    });
  }

  // ── Rule 6: Verdict is DIVERGENT → investigate divergence ────
  // Divergent findings are surprises — opposite of expected. They
  // are the most interesting research moments, not failures.
  if (verdict === 'divergent') {
    moves.push({
      id: 'investigate-divergence',
      priority: 'high',
      label: 'Investigate why eco and human diverge here',
      why: 'A divergent finding signals the eco-human parallel breaks. Why? That mechanism is the finding worth pursuing.',
      duration: 'varies · 1-2 sessions of analysis',
      status: 'available',
    });
  }

  // ── Rule 7: INSUFFICIENT data → propose data collection ──────
  if (verdict === 'insufficient_data' || verdict === 'insuf') {
    moves.push({
      id: 'collect-more-data',
      priority: 'medium',
      label: 'Expand the dataset for this dynamic',
      why: 'Insufficient sample size on at least one side. Identify the source-key with the smallest n and prioritize wiring more events.',
      duration: 'varies · per source',
      status: 'available',
    });
  }

  // ── Rule 8: Anything that survived Phase A respec → revisit ──
  // A finding that survived RESPECIFIED has been weakened. The
  // user should revisit the original claim and adjust language
  // throughout dependent records.
  if (phaseAStatus === 'survived-respecified') {
    moves.push({
      id: 'cascade-respec',
      priority: 'medium',
      label: 'Cascade the respec through dependent findings',
      why: 'This finding survived Phase A in respecified form. Other findings citing it inherit the weakened scope and need their claims softened.',
      duration: '~5 min',
      status: 'available',
    });
  }

  // Sort: high → medium → low
  return moves.sort((a, b) => {
    const rank = { high: 0, medium: 1, low: 2 };
    return (rank[a.priority] ?? 99) - (rank[b.priority] ?? 99);
  });
}

// ─── UI component ─────────────────────────────────────────────
export function NextMovesPanel({ comp, dyn, workspaceId, onCompUpdate }) {
  // Local copy of comp so we can re-render after Phase A completes
  // without waiting on a parent reload. Parent gets notified via
  // onCompUpdate so it can sync if it cares.
  const [localComp, setLocalComp] = useState(comp);
  const [busyMoveId, setBusyMoveId] = useState(null);
  const [error, setError] = useState(null);
  // Inline form expansion (forward-prediction logger + parallel
  // hypothesis registration both live here, mutually exclusive).
  const [expandedMoveId, setExpandedMoveId] = useState(null);
  // Track moves that have logged a prediction in this session, so the
  // row swaps to a "Logged" confirmation card instead of re-offering.
  // Pre-populated on mount from existing DB rows so page-refresh
  // doesn't re-offer for findings that already have a prediction.
  const [loggedPrediction, setLoggedPrediction] = useState(null); // { id, claim, resolution_date }
  const [registeredHypothesis, setRegisteredHypothesis] = useState(null); // { id, eco_claim, human_claim, registered_at }
  // Ref-guard for double-click race. setBusyMoveId is async; a second
  // click before the next render would slip through the !busy check.
  const inFlightRef = useRef(false);

  const effectiveComp = localComp || comp;
  const moves = generateNextMoves({
    comp: effectiveComp,
    dyn,
    hasParallelHypothesis: !!registeredHypothesis,
  });

  // On mount, check if this comparison already has a prediction or
  // a parallel hypothesis. Fire-and-forget — silent failure is OK
  // because the worst case is a duplicate row, which is harmless.
  useEffect(() => {
    if (!workspaceId || !effectiveComp?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [predRes, hypRes] = await Promise.all([
          loggedPrediction
            ? null
            : fetch(`/api/continuum/predictions/list?workspace_id=${workspaceId}&comparison_id=${effectiveComp.id}`),
          registeredHypothesis
            ? null
            : fetch(`/api/continuum/hypotheses/list?workspace_id=${workspaceId}&comparison_id=${effectiveComp.id}`),
        ]);
        if (predRes?.ok) {
          const j = await predRes.json();
          const latest = (j.predictions || [])[0];
          if (!cancelled && latest) {
            setLoggedPrediction({
              id: latest.id,
              claim: latest.claim,
              resolution_date: latest.resolution_date,
            });
          }
        }
        if (hypRes?.ok) {
          const j = await hypRes.json();
          const latest = (j.hypotheses || [])[0];
          if (!cancelled && latest) {
            setRegisteredHypothesis({
              id: latest.id,
              eco_claim: latest.eco_claim,
              human_claim: latest.human_claim,
              registered_at: latest.registered_at,
            });
          }
        }
      } catch {
        // silent — duplicates are harmless
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, effectiveComp?.id, loggedPrediction, registeredHypothesis]);

  const handleAction = async (move) => {
    if (move.id === 'run-phase-a') {
      if (inFlightRef.current) return;
      if (!workspaceId || !effectiveComp?.id) {
        setError('Missing workspace or comparison id');
        return;
      }
      inFlightRef.current = true;
      setBusyMoveId('run-phase-a');
      setError(null);
      try {
        const res = await fetch('/api/continuum/phase-a/run', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workspace_id: workspaceId,
            comparison_id: effectiveComp.id,
          }),
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || `HTTP ${res.status}`);
        }
        const j = await res.json();
        const updated = j.comparison || {
          ...effectiveComp,
          phase_a_status: j.phase_a_status,
          phase_a_modules: j.phase_a_modules,
          phase_a_run_at: j.phase_a_run_at,
        };
        setLocalComp(updated);
        onCompUpdate?.(updated);
      } catch (err) {
        setError(err.message);
      } finally {
        inFlightRef.current = false;
        setBusyMoveId(null);
      }
      return;
    }

    if (move.id === 'log-forward-prediction' || move.id === 'register-parallel-hypothesis') {
      // Toggle the inline form. The form's own submit handler hits
      // the matching create endpoint.
      setExpandedMoveId(prev => prev === move.id ? null : move.id);
      setError(null);
      return;
    }
    // Other action ids are still informational — wired in
    // subsequent extension sessions (8: open questions, 9-12:
    // brief generators).
  };

  const handleSavePrediction = async (formData) => {
    if (!workspaceId || !effectiveComp?.id) {
      throw new Error('Missing workspace or comparison id');
    }
    const res = await fetch('/api/continuum/predictions/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workspace_id: workspaceId,
        comparison_id: effectiveComp.id,
        ...formData,
      }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `HTTP ${res.status}`);
    }
    const j = await res.json();
    setLoggedPrediction({
      id: j.prediction.id,
      claim: j.prediction.claim,
      resolution_date: j.prediction.resolution_date,
    });
    setExpandedMoveId(null);
  };

  const handleSaveHypothesis = async (formData) => {
    if (!workspaceId || !effectiveComp?.id) {
      throw new Error('Missing workspace or comparison id');
    }
    const res = await fetch('/api/continuum/hypotheses/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workspace_id: workspaceId,
        comparison_id: effectiveComp.id,
        ...formData,
      }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `HTTP ${res.status}`);
    }
    const j = await res.json();
    setRegisteredHypothesis({
      id: j.hypothesis.id,
      eco_claim: j.hypothesis.eco_claim,
      human_claim: j.hypothesis.human_claim,
      registered_at: j.hypothesis.registered_at,
    });
    setExpandedMoveId(null);
  };

  const phaseARanWithoutVerdict = !!effectiveComp?.phase_a_run_at &&
                                   !effectiveComp?.phase_a_status;

  if (!moves.length) {
    return (
      <div style={{
        padding: '10px 12px',
        background: 'rgba(122,158,130,.04)',
        border: `1px solid ${C.b1}`,
        borderRadius: 5,
      }}>
        <div style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
          color: C.dim, textTransform: 'uppercase', marginBottom: 6,
        }}>
          ◆ What to do next
        </div>
        <div style={{
          fontSize: 11, color: C.dim, lineHeight: 1.5, fontFamily: F.sans,
          fontStyle: 'italic',
        }}>
          {effectiveComp?.phase_a_status
            ? `Phase A complete — verdict: ${effectiveComp.phase_a_status.toUpperCase()}. New moves will appear as the finding's state evolves.`
            : phaseARanWithoutVerdict
            ? "Phase A ran but couldn't extract a clean verdict. Module output is stored — see admin / API for the raw response."
            : 'No next moves at this stage — finding is still pending or has no actionable state yet.'}
        </div>
        {effectiveComp?.phase_a_status && (
          <PhaseAVerdictStrip phaseAStatus={effectiveComp.phase_a_status} runAt={effectiveComp.phase_a_run_at} />
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(200,169,110,.05)',
      border: `1px solid rgba(200,169,110,.30)`,
      borderRadius: 5,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.gold, textTransform: 'uppercase', marginBottom: 8,
        fontWeight: 500,
      }}>
        ◆ What to do next ({moves.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {moves.map(move => {
          if (move.id === 'log-forward-prediction' && loggedPrediction) {
            return (
              <PredictionLoggedCard key={move.id} prediction={loggedPrediction} />
            );
          }
          if (move.id === 'register-parallel-hypothesis' && registeredHypothesis) {
            return (
              <HypothesisRegisteredCard key={move.id} hypothesis={registeredHypothesis} />
            );
          }
          return (
            <div key={move.id}>
              <NextMoveRow
                move={move}
                busy={busyMoveId === move.id}
                expanded={expandedMoveId === move.id}
                onAction={() => handleAction(move)}
              />
              {move.id === 'log-forward-prediction' && expandedMoveId === move.id && (
                <PredictionForm
                  comp={effectiveComp}
                  dyn={dyn}
                  hypothesisId={registeredHypothesis?.id || null}
                  onCancel={() => setExpandedMoveId(null)}
                  onSave={handleSavePrediction}
                />
              )}
              {move.id === 'register-parallel-hypothesis' && expandedMoveId === move.id && (
                <HypothesisForm
                  comp={effectiveComp}
                  dyn={dyn}
                  onCancel={() => setExpandedMoveId(null)}
                  onSave={handleSaveHypothesis}
                />
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 4,
          fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          Phase A error: {error}
        </div>
      )}
      {effectiveComp?.phase_a_status && (
        <PhaseAVerdictStrip phaseAStatus={effectiveComp.phase_a_status} runAt={effectiveComp.phase_a_run_at} />
      )}
      {phaseARanWithoutVerdict && (
        <PhaseAAmbiguousStrip runAt={effectiveComp.phase_a_run_at} />
      )}
    </div>
  );
}

// ─── Phase A ambiguous-result strip ───────────────────────────
// Phase A modules ran, output was stored, but the aggregator
// couldn't extract a clean SURVIVED / KILLED / RESPECIFIED verdict.
// Show this once so the user knows the run happened (and they
// shouldn't spend another $0.20 re-running) and that the module
// output is still available for inspection.
function PhaseAAmbiguousStrip({ runAt }) {
  return (
    <div style={{
      marginTop: 12, padding: '8px 12px',
      background: `${C.dim}10`,
      border: `1px solid ${C.dim}40`,
      borderRadius: 4,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
          color: C.dim, textTransform: 'uppercase', fontWeight: 600,
        }}>
          ◆ Phase A · ambiguous result
        </span>
        <span style={{
          fontFamily: F.sans, fontSize: 11, color: C.mid, lineHeight: 1.4,
        }}>
          Modules ran. Aggregator couldn't extract a clean verdict — raw output stored.
        </span>
      </div>
      {runAt && (
        <span style={{
          fontFamily: F.mono, fontSize: 9, color: C.dim,
          letterSpacing: '0.04em',
        }}>
          {new Date(runAt).toLocaleString()}
        </span>
      )}
    </div>
  );
}

// Move ids that have wired interactive behavior. Other available
// moves still render as informational until their session ships.
const INTERACTIVE_MOVE_IDS = new Set([
  'run-phase-a',
  'log-forward-prediction',
  'register-parallel-hypothesis',
]);

function NextMoveRow({ move, busy = false, expanded = false, onAction }) {
  const priorityColor = PRIORITY_COLOR[move.priority] || C.dim;
  const statusColor = STATUS_COLOR[move.status] || C.dim;
  const isClickable = move.status === 'available' && INTERACTIVE_MOVE_IDS.has(move.id);

  const handleClick = (e) => {
    e.stopPropagation(); // don't collapse the parent row
    if (isClickable && !busy) onAction?.();
  };

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: '8px 1fr auto',
        gap: 10,
        alignItems: 'baseline',
        padding: '6px 4px',
        borderRadius: 3,
        borderBottom: `1px dashed ${C.b1}`,
        cursor: isClickable && !busy ? 'pointer' : 'default',
        background: isClickable ? 'rgba(122,158,130,.04)' : 'transparent',
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        if (isClickable && !busy) e.currentTarget.style.background = 'rgba(122,158,130,.10)';
      }}
      onMouseLeave={(e) => {
        if (isClickable && !busy) e.currentTarget.style.background = 'rgba(122,158,130,.04)';
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: priorityColor,
        marginTop: 6,
      }} />
      <div>
        <div style={{
          fontSize: 12, color: C.text, lineHeight: 1.4,
          fontFamily: F.sans, fontWeight: 500,
        }}>
          {move.label}
        </div>
        <div style={{
          fontSize: 10, color: C.mid, lineHeight: 1.5,
          marginTop: 3, fontFamily: F.sans,
        }}>
          {move.why}
        </div>
        <div style={{
          fontSize: 9, color: C.dim, marginTop: 4,
          fontFamily: F.mono, letterSpacing: '0.04em',
        }}>
          {move.duration}
          {move.status === 'coming' && move.sessionAt && (
            <span style={{ color: statusColor, marginLeft: 8 }}>
              · {move.sessionAt}
            </span>
          )}
        </div>
      </div>
      <span style={{
        fontSize: 8, padding: '2px 7px', borderRadius: 3,
        border: `1px solid ${statusColor}50`,
        background: `${statusColor}10`, color: statusColor,
        letterSpacing: '0.06em', fontFamily: F.mono,
        whiteSpace: 'nowrap', textTransform: 'uppercase',
      }}>
        {busy ? 'running…' : move.status}
      </span>
    </div>
  );
}

// ─── Forward-prediction logger form ───────────────────────────
// Pre-fills claim from the finding's human_translation, with
// Phase A residual_caveat appended when the finding survived
// respecified (the respec is what makes the claim sharper).
// Pre-fills resolution_criteria from ecological_prescription.
// User can edit anything before saving.
function PredictionForm({ comp, dyn, hypothesisId, onCancel, onSave }) {
  const prefilledClaim = (() => {
    const base = dyn?.human_translation || comp?.finding_text || '';
    const residual = comp?.phase_a_modules?.counter_narrative?.residual_caveat;
    if (!base) return '';
    if (comp?.phase_a_status === 'survived-respecified' && residual) {
      return `${base}\n\nRespecified per Phase A: ${residual}`;
    }
    return base;
  })();

  const defaultResolutionDate = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return d.toISOString().slice(0, 10);
  })();

  // Map the comp's confidence (HIGH/MEDIUM/LOW) to the prediction
  // confidence vocabulary (high/med-high/med-low/low). Phase A
  // verdict influences the default: respecified pulls down a tier.
  const defaultConfidence = (() => {
    const cf = (comp?.confidence || '').toLowerCase();
    const respecified = comp?.phase_a_status === 'survived-respecified';
    if (cf === 'high') return respecified ? 'med-high' : 'high';
    if (cf === 'medium') return respecified ? 'med-low' : 'med-high';
    if (cf === 'low') return 'low';
    return 'med-low';
  })();

  const [claim, setClaim] = useState(prefilledClaim);
  const [resolutionDate, setResolutionDate] = useState(defaultResolutionDate);
  const [resolutionCriteria, setResolutionCriteria] = useState(dyn?.ecological_prescription || '');
  const [confidence, setConfidence] = useState(defaultConfidence);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!claim.trim() || claim.trim().length < 10) {
      setFormError('Claim must be at least 10 characters');
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      await onSave({
        claim: claim.trim(),
        resolution_date: resolutionDate,
        resolution_criteria: resolutionCriteria.trim() || null,
        confidence_at_registration: confidence,
        hypothesis_id: hypothesisId || null,
      });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 8, padding: '12px',
        background: 'rgba(155,143,196,.06)',
        border: `1px solid rgba(155,143,196,.30)`,
        borderRadius: 4,
      }}
    >
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.purple || C.gold, textTransform: 'uppercase',
        marginBottom: 10, fontWeight: 500,
      }}>
        ◆ Register forward prediction
      </div>

      {hypothesisId && (
        <div style={{
          marginBottom: 10, padding: '6px 10px',
          background: 'rgba(77,184,184,.08)',
          border: `1px solid ${C.teal}40`, borderRadius: 3,
          fontFamily: F.mono, fontSize: 9, color: C.teal,
          letterSpacing: '0.04em',
        }}>
          ✓ Will be linked to the parallel hypothesis registered for this finding
        </div>
      )}

      <PredictionField label="Claim">
        <textarea
          value={claim}
          onChange={(e) => setClaim(e.target.value)}
          rows={4}
          placeholder="State the predicted relationship as a falsifiable claim…"
          style={INPUT_STYLE}
        />
      </PredictionField>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
        <PredictionField label="Resolution date">
          <input
            type="date"
            value={resolutionDate}
            onChange={(e) => setResolutionDate(e.target.value)}
            style={INPUT_STYLE}
          />
        </PredictionField>
        <PredictionField label="Confidence at registration">
          <select
            value={confidence}
            onChange={(e) => setConfidence(e.target.value)}
            style={INPUT_STYLE}
          >
            <option value="high">high</option>
            <option value="med-high">med-high</option>
            <option value="med-low">med-low</option>
            <option value="low">low</option>
          </select>
        </PredictionField>
      </div>

      <div style={{ marginTop: 10 }}>
        <PredictionField label="Resolution criteria (how will we know if it came true?)">
          <textarea
            value={resolutionCriteria}
            onChange={(e) => setResolutionCriteria(e.target.value)}
            rows={3}
            placeholder="What evidence at the resolution date would confirm or refute this?"
            style={INPUT_STYLE}
          />
        </PredictionField>
      </div>

      {formError && (
        <div style={{
          marginTop: 10, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3,
          fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {formError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={BUTTON_SECONDARY}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          style={BUTTON_PRIMARY}
        >
          {saving ? 'Saving…' : 'Register prediction'}
        </button>
      </div>
    </div>
  );
}

function PredictionField({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{
        display: 'block',
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: C.dim, textTransform: 'uppercase',
        marginBottom: 4,
      }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const INPUT_STYLE = {
  width: '100%',
  background: 'rgba(255,255,255,.02)',
  border: `1px solid ${C.b1}`,
  borderRadius: 3,
  padding: '6px 8px',
  fontFamily: 'inherit',
  fontSize: 12,
  color: C.text,
  resize: 'vertical',
  outline: 'none',
  boxSizing: 'border-box',
};

const BUTTON_PRIMARY = {
  background: C.gold,
  border: `1px solid ${C.gold}`,
  borderRadius: 3,
  padding: '6px 14px',
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: '0.06em',
  color: '#1a1a1a',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontWeight: 600,
};

const BUTTON_SECONDARY = {
  background: 'transparent',
  border: `1px solid ${C.b1}`,
  borderRadius: 3,
  padding: '6px 14px',
  fontFamily: F.mono,
  fontSize: 10,
  letterSpacing: '0.06em',
  color: C.dim,
  textTransform: 'uppercase',
  cursor: 'pointer',
};

// ─── Parallel-hypothesis registration form ────────────────────
// Pre-fills eco_claim from ecological_prescription, human_claim
// from human_translation. shared_mechanism + testable_predictions
// start blank — those are the user's own analytical contribution
// that gives the registration its value.
function HypothesisForm({ comp, dyn, onCancel, onSave }) {
  const [ecoClaim, setEcoClaim] = useState(dyn?.ecological_prescription || '');
  const [humanClaim, setHumanClaim] = useState(dyn?.human_translation || comp?.finding_text || '');
  const [sharedMechanism, setSharedMechanism] = useState('');
  const [testablePredictions, setTestablePredictions] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!ecoClaim.trim() || ecoClaim.trim().length < 10) {
      setFormError('Eco claim must be at least 10 characters');
      return;
    }
    if (!humanClaim.trim() || humanClaim.trim().length < 10) {
      setFormError('Human claim must be at least 10 characters');
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      await onSave({
        eco_claim: ecoClaim.trim(),
        human_claim: humanClaim.trim(),
        shared_mechanism: sharedMechanism.trim() || null,
        testable_predictions: testablePredictions.trim() || null,
      });
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 8, padding: '12px',
        background: 'rgba(77,184,184,.06)',
        border: `1px solid rgba(77,184,184,.30)`,
        borderRadius: 4,
      }}
    >
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.teal || C.gold, textTransform: 'uppercase',
        marginBottom: 10, fontWeight: 500,
      }}>
        ◆ Register parallel hypothesis
      </div>

      <PredictionField label="Ecological claim">
        <textarea
          value={ecoClaim}
          onChange={(e) => setEcoClaim(e.target.value)}
          rows={3}
          placeholder="State the eco-side claim — what the mechanism produces in ecological systems."
          style={INPUT_STYLE}
        />
      </PredictionField>

      <div style={{ marginTop: 10 }}>
        <PredictionField label="Human-system claim">
          <textarea
            value={humanClaim}
            onChange={(e) => setHumanClaim(e.target.value)}
            rows={3}
            placeholder="State the human-side claim — what the same mechanism produces in human systems."
            style={INPUT_STYLE}
          />
        </PredictionField>
      </div>

      <div style={{ marginTop: 10 }}>
        <PredictionField label="Shared mechanism (what is the same across both systems?)">
          <textarea
            value={sharedMechanism}
            onChange={(e) => setSharedMechanism(e.target.value)}
            rows={3}
            placeholder="Name the structural mechanism that connects the two claims. This is the load-bearing assumption of the parallel."
            style={INPUT_STYLE}
          />
        </PredictionField>
      </div>

      <div style={{ marginTop: 10 }}>
        <PredictionField label="Refuters (what evidence in either domain would refute the parallel?)">
          <textarea
            value={testablePredictions}
            onChange={(e) => setTestablePredictions(e.target.value)}
            rows={3}
            placeholder="Be specific. Vague refuters don't sharpen the claim."
            style={INPUT_STYLE}
          />
        </PredictionField>
      </div>

      {formError && (
        <div style={{
          marginTop: 10, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3,
          fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {formError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={BUTTON_SECONDARY}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          style={{ ...BUTTON_PRIMARY, background: C.teal || C.gold, borderColor: C.teal || C.gold }}
        >
          {saving ? 'Saving…' : 'Register hypothesis'}
        </button>
      </div>
    </div>
  );
}

// ─── Registered-hypothesis confirmation card ──────────────────
// Replaces the row after a successful save. Shows both halves of
// the parallel claim so the user has visual feedback without
// needing to navigate to the (Session 6) Hypotheses tab.
function HypothesisRegisteredCard({ hypothesis }) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(77,184,184,.06)',
      border: `1px solid rgba(77,184,184,.40)`,
      borderRadius: 4,
      borderBottom: `1px dashed ${C.b1}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 8,
      }}>
        <span style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
          color: C.teal || C.gold, textTransform: 'uppercase', fontWeight: 600,
        }}>
          ✓ Parallel hypothesis registered
        </span>
        <span style={{
          fontFamily: F.mono, fontSize: 9, color: C.dim,
          letterSpacing: '0.04em',
        }}>
          {hypothesis.registered_at ? new Date(hypothesis.registered_at).toLocaleDateString() : '—'}
        </span>
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <ClaimRow label="Eco" text={hypothesis.eco_claim} />
        <ClaimRow label="Human" text={hypothesis.human_claim} />
      </div>
    </div>
  );
}

function ClaimRow({ label, text }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 8, alignItems: 'baseline' }}>
      <span style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: C.dim, textTransform: 'uppercase',
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 11, color: C.mid, lineHeight: 1.5, fontFamily: F.sans,
        whiteSpace: 'pre-wrap',
      }}>
        {text}
      </span>
    </div>
  );
}

// ─── Logged-prediction confirmation card ──────────────────────
// Replaces the "Log a forward prediction" row after a successful
// save. Shows the registered claim + resolution date so the user
// has visual feedback without needing to scroll to a registry.
function PredictionLoggedCard({ prediction }) {
  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(122,158,130,.08)',
      border: `1px solid rgba(122,158,130,.40)`,
      borderRadius: 4,
      borderBottom: `1px dashed ${C.b1}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 6,
      }}>
        <span style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
          color: C.sage, textTransform: 'uppercase', fontWeight: 600,
        }}>
          ✓ Forward prediction logged
        </span>
        <span style={{
          fontFamily: F.mono, fontSize: 9, color: C.dim,
          letterSpacing: '0.04em',
        }}>
          resolves {new Date(prediction.resolution_date).toLocaleDateString()}
        </span>
      </div>
      <div style={{
        fontSize: 11, color: C.mid, lineHeight: 1.5, fontFamily: F.sans,
        whiteSpace: 'pre-wrap',
      }}>
        {prediction.claim}
      </div>
    </div>
  );
}

// ─── Phase A verdict strip (shown after a successful run) ─────
function PhaseAVerdictStrip({ phaseAStatus, runAt }) {
  const verdictColor = {
    survived: C.sage,
    'survived-respecified': C.gold,
    killed: C.terra,
  }[phaseAStatus] || C.dim;

  const verdictLabel = {
    survived: 'SURVIVED',
    'survived-respecified': 'SURVIVED · RESPECIFIED',
    killed: 'KILLED',
  }[phaseAStatus] || phaseAStatus.toUpperCase();

  return (
    <div style={{
      marginTop: 12, padding: '8px 12px',
      background: `${verdictColor}10`,
      border: `1px solid ${verdictColor}50`,
      borderRadius: 4,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
          color: verdictColor, textTransform: 'uppercase', fontWeight: 600,
        }}>
          ◆ Phase A Verdict
        </span>
        <span style={{
          fontFamily: F.mono, fontSize: 11, color: verdictColor,
          fontWeight: 600, letterSpacing: '0.04em',
        }}>
          {verdictLabel}
        </span>
      </div>
      {runAt && (
        <span style={{
          fontFamily: F.mono, fontSize: 9, color: C.dim,
          letterSpacing: '0.04em',
        }}>
          {new Date(runAt).toLocaleString()}
        </span>
      )}
    </div>
  );
}
